import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import shapes from './shapes';
import text from '@ux/text';

import CreditCard from '@ux/icon/credit-card';
import Alert from '@ux/icon/alert';
import ChevronRight from '@ux/icon/chevron-right';

/**
 * Renders individual notification list item.
 *
 * @class NotificationListItem
 * @public
 *
 * @returns {React.ReactElement} Notifications
 */

class NotificationListItem extends Component {
  constructor(props) {
    super(props);
    this.renderNotificationLink = this.renderNotificationLink.bind(this);
    this.renderStaticNotification = this.renderStaticNotification.bind(this);
  }

  /**
   * Renders notification with link when link is available.
   *
   * @typedef {Object} Icon Visual indicator type for a credit card or alert icon
   *
   * @params {Object} notification Single notification
   * @params {Object} [notification.eids] Single notification's EID info
   * @params {sting} [notification.eids.click] Single notification click Eid
   * @params {string} notification.content Single notification
   * @params {string} href The link for the notification
   * @params {array} classes List of needed classes
   * @params {Icon} icon Visual indicator type icon
   *
   * @private
   * @returns {React.ReactElement} Individual notification
   */

  renderNotificationLink(notification, href, classes, icon) {
    const { content, eids } = notification;

    return (
      <>
        <a
          target='_blank'
          rel='noopener noreferrer'
          href={ href }
          data-eid={ eids && eids.click }
          className={ classes }>
          <div className='priority-icon'>{ icon }</div>
          <div className='priority-indicator' />
          <text.div className='notification-content' text={ content } />
          <span className='icon-notification-link'>
            <ChevronRight />
          </span>
        </a>
      </>
    );
  }

  /**
   * Renders notification with cta and message when cta is available.
   *
   * @typedef {Object} Icon Visual indicator type for a credit card or alert icon
   *
   * @params {Object} notification Single notification
   * @params {Object} [notification.eids] Single notification's EID info
   * @params {sting} [notification.eids.click] Single notification click Eid
   * @params {string} notification.title Title of the notification
   * @params {string} notification.message Sub title for the notification
   * @params {string} cta The cta info for the notification
   * @params {string} cta.text The text for the notification cta button
   * @params {string} cta.url The link for the notification cta button
   * @params {array} classes List of needed classes
   * @params {Icon} icon Visual indicator type icon
   *
   * @private
   * @returns {React.ReactElement} Individual notification
   */

  renderRADNotification(notification, classes) {
    const { title, message, eids, cta, section } = notification;
    const hasSection = !!section;

    let ctaURL = cta.url;

    if (!ctaURL?.match?.(/^https?:\/\//)) {
      ctaURL = '';
    }

    return (
      <React.Fragment>
        <ul className={ classnames(classes, 'rad-container') }>
          {hasSection && (
            <text.li className='rad-section-heading' text={ section } />
          )}
          <text.li className='rad-title' text={ title } />
          <text.li className='rad-content' text={ message } />
          { ctaURL && cta.text &&
            <li className='rad-link'>
              <a href={ ctaURL } target='_self' data-eid={ eids && eids.click }>
                {cta.text}
              </a>
            </li>
          }
        </ul>

      </React.Fragment>
    );
  }

  /**
   * Renders static (no link) notification.
   *
   * @returns {React.ReactElement} Individual notification
   *
   * @params {Object} notification Single notification
   * @params {string} [notification.priority] level of priority EX: 'medium' or 'high'
   * @params {string} [notification.subcategory] Notification category EX:'FailedBilling'
   * @params {string} [notification.link] URl notification points to.
   * @params {array} classes List of needed classes
   * @params {Icon} icon Visual indicator type icon
   *
   * @private
   */

  renderStaticNotification(notification, classes, icon) {
    return (
      <span className={ classnames(classes, 'no-link') }>
        { icon }
        { notification.content }
      </span>
    );
  }

  render() {
    const { notification, index } = this.props;
    const { priority, subcategory, link, eids = {} } = notification;

    if (eids.impression && eids.impression.includes('holdout')) {
      return null;
    }

    const type = subcategory === 'FailedBilling' ? 'creditCardExpiration' : '';
    const priorityClassMap = {
      Medium: 'medium',
      High: 'high'
    };
    const priorityClass = priorityClassMap[priority] || '';
    const priorityLabelClassName = classnames(
      'priority',
      priorityClass,
      type
    );
    const classes = ['font-base'];
    const icon = type === 'creditCardExpiration' ? <CreditCard /> : <Alert />;

    let notifyBody;

    if (notification.isRad) {
      classes.push('notifications-rad-list-item');
      notifyBody = this.renderRADNotification(
        notification,
        priorityLabelClassName,
        icon);
    } else if (link) {
      classes.push('notifications-list-item');
      notifyBody = this.renderNotificationLink(
        notification,
        link,
        priorityLabelClassName,
        icon
      );
    } else {
      classes.push('notifications-list-item');
      notifyBody = this.renderStaticNotification(
        notification,
        priorityLabelClassName,
        icon
      );
    }

    return (
      <li className={ classnames(classes) } id={ `notifications-list-item-${index}` }>
        {notifyBody}
        <div className='notification-divider' />
      </li>
    );
  }
}

/**
 * Property definitions.
 *
 * @type {Object}
 * @public
 *
 */

NotificationListItem.propTypes = {
  /**  User notifications */
  notification: shapes.notification.isRequired,
  /** Order of individual notification */
  index: PropTypes.number
};

export default NotificationListItem;
